import context from '@truckdown/components-systems';
import { AddressStatus, IAddressValidator, IAuthenticator, LoginWithValidationTokenModel, ValidationResultModel } from '@truckdown/systems';
import { IUserManager, UserModel } from "@truckdown/accounts";
import { defineComponent, ref, Ref } from "vue";
import { isCsrOrAdmin } from "../../../Utilities/Utilities";
import { ChangeMobileModel } from '@truckdown/accounts/Users/ChangeMobileModel';

import { useVuelidate, Validation } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

const phone = (value: string) => /^$|\+?(1|\d{3})?[-. ]?\(?(\d{3})\)?[-. ]?(\d{3})[-. ]?(\d{4})$/.test(value);

interface IData {
    isLoading: Ref<boolean>,
    showAddressValidator: Ref<boolean>,
    usernameResetTrigger: Ref<number>,
    newMobile: Ref<string>,
    userProfile: Ref<UserModel>,
    errorMessage: Ref<string>,

    mobileOptin: Ref<boolean>,
    terms: Ref<boolean>,
    showMobileOptinError: Ref<boolean>,
    showMobileTermsError: Ref<boolean>,
    validationResult: Ref<ValidationResultModel | undefined>
};

const validations = {
    newMobile: {
        phone,
        $autoDirty: true
    }
}

const getInitialData = function (props: any) {
    return {
        isLoading: ref<boolean>(false),
        showAddressValidator: ref<boolean>(true),
        usernameResetTrigger: ref<number>(0),
        newMobile: ref<string>(''),
        userProfile: ref<UserModel>(props.userProfile),
        errorMessage: ref<string>(''),

        mobileOptin: ref<boolean>(false),
        terms: ref<boolean>(false),
        showMobileOptinError: ref<boolean>(false),
        showMobileTermsError: ref<boolean>(false),
        validationResult: ref<ValidationResultModel | undefined>()
    } as IData;
}

const getMethods = function (props: any, data: IData, ctx: any, validator: Ref<Validation>) {

    const validate = function () {
        validator.value.$touch();
        return validator.value.$validate()
            .then((res) => {
                return res;
            });
    }

    const updateMobileNumber = async function (res: ValidationResultModel) {
        var userManager = await context.getService<IUserManager>('IUserManager');
        let changeMobileResult = await userManager.changeMobile(
            {
                id: props.userProfile.id,
                mobile: res.address,
                mobileToken: res.validationToken
            } as ChangeMobileModel
        );

        if (changeMobileResult && changeMobileResult.ok) {
            var authenticator = await context.getService<IAuthenticator>('IAuthenticator');
            var loginResult = await authenticator.login({
                username: res.address,
                validationToken: res.validationToken
            } as LoginWithValidationTokenModel);

            if (loginResult && loginResult.ia) {
                data.userProfile.value.mobile = res.address;

                ctx.emit(userUpdatedEventName, data.userProfile.value);
            } else {
                data.errorMessage.value = 'Error updating mobile number';
            }
        } else {
            data.errorMessage.value = "Updated successfully, please re-login for changes to take effect.";
        }
    }

    return {
        editMobileNumberCancelClick: function (): void {
            ctx.emit(userCancelEventName);
        },
        updateMobileSkip: async function () {
            if (!data.isLoading.value) {
                data.isLoading.value = true;

                validate().then(async (res) => {
                    if (res) {
                        try {
                            data.errorMessage.value = '';
                            var userManager = await context.getService<IUserManager>('IUserManager');

                            var model = {
                                id: props.userProfile.id,
                                mobile: data.newMobile.value,
                                mobileToken: ""
                            } as ChangeMobileModel;

                            let changeMobileResult = await userManager.changeMobile(model);

                            if (changeMobileResult && changeMobileResult.ok) {
                                data.userProfile.value.mobile = data.newMobile.value;
                                ctx.emit('update', changeMobileResult.data);
                            } else {
                                data.errorMessage.value = "Error updating mobile number: " + changeMobileResult.message;
                            }
                        } catch (err) {
                            data.errorMessage.value = "Error updating mobile number.";
                        } finally {
                            data.isLoading.value = false;
                        }
                    } else {
                        data.isLoading.value = false;
                    }
                });
            }
        },
        updateMobile: async function (type: string, res: ValidationResultModel) {
            if (res?.status == AddressStatus.Active || res?.status == AddressStatus.Inactive) {
                data.errorMessage.value = `${res.address} already exists in our system.`
            } else if (!data.mobileOptin.value || !data.terms.value) {
                data.validationResult.value = res;
                if (!data.mobileOptin.value) {
                    data.showMobileOptinError.value = true;
                }
                if (!data.terms.value) {
                    data.showMobileTermsError.value = true;
                }
            } else {
                data.errorMessage.value = '';
                await updateMobileNumber(res);
            }
        },
        continueUpdateMobile: async function () {
            data.errorMessage.value = '';
            if (data.mobileOptin.value) {
                data.showMobileOptinError.value = false;
            } else {
                data.showMobileOptinError.value = true;
            }
            if (data.terms.value) {
                data.showMobileTermsError.value = false;
            } else {
                data.showMobileTermsError.value = true;
            }

            if (data.mobileOptin.value && data.terms.value) {
                await updateMobileNumber(data.validationResult.value as ValidationResultModel);
            }
        }
    }
}

const userUpdatedEventName = 'update';
const userCancelEventName = 'cancel';

export default defineComponent({
    props: {
        userProfile: {
            type: Object as () => UserModel,
            required: true
        },
        isAdmin: Boolean
    },
    components: {
    },
    emits: [userUpdatedEventName, userCancelEventName],
    setup: function (props, ctx) {

        let data = getInitialData(props);

        let v$ = useVuelidate(validations, data);

        let methods = getMethods(props, data, ctx, v$);

        return {
            ...data,
            ...methods,
            v$
        };
    }
});